<script lang="ts" setup>
import type { BlogCollectionItem, CollectionQueryBuilder, CollectionQueryGroup } from '@nuxt/content';
import { object, string } from 'yup';

import blogLogo from '~/assets/icons/logo/blog.svg?url';

const { t } = useI18n();
const applyApprovedFilter = useArticleApprovedFilter();

const prepareArticleFilter = <
  T extends CollectionQueryGroup<BlogCollectionItem> | CollectionQueryBuilder<BlogCollectionItem>,
>(
  qb: T,
) => applyApprovedFilter(qb.where('section_blog', '=', 1).where('category', '=', 'articles') as T);

const [{ data: articles }, { data: articlesCount }] = await Promise.all([
  useAsyncData('articles', () =>
    queryCollection('blog')
      .order('order', 'ASC')
      .andWhere((qb) => prepareArticleFilter(qb))
      .limit(7)
      .select('path', 'title', 'image', 'category', 'downloadUrl')
      .all(),
  ),
  useAsyncData('articles-count', () => prepareArticleFilter(queryCollection('blog')).count(), {
    dedupe: 'defer',
  }),
]);

const route = useValidatedRoute({
  params: object().shape({
    category: string()
      .nullable()
      .transform((value) => value || null)
      .oneOf(BLOG_CATEGORIES.map((category) => category.code)),
  }),
});

assertNotEmpty(articles, () => createError({ statusCode: 404, statusMessage: 'Page Not Found' }));
assertNotEmpty(articlesCount, () => createError({ statusCode: 404, statusMessage: 'Page Not Found' }));

const isLoading = ref(false);

const categories = computed(() => [
  { value: 'all', label: t('category_all'), default: true },
  ...BLOG_CATEGORIES.map((el) => ({
    value: el.code,
    label: t(`blog.category.${el.code}`),
    color: el.color,
  })),
]);
const selectedCategory = computed(() => route.params.category || 'all');

const filteredArticles = computed(() =>
  articles.value.filter((article) => !route.params.category || route.params.category === article.category),
);

// TODO: 13.10.2025 + где-то пол года и эту логику можно удалить.
//  Она нужна исключительно в связи с миграцией категорий из get парамеров в параметры пути.
const nativeRoute = useRoute();
if (nativeRoute.query.categories) {
  const category = (
    typeof nativeRoute.query.categories === 'string' ? nativeRoute.query.categories : nativeRoute.query.categories[0]
  )?.split(',')[0];
  if (category) {
    if (categories.value.some((section) => section.value === category)) await navigateTo(`/blog/${category}`);
    else await navigateTo(`/article/${category}`);
  }
}

const showMore = async () => {
  if (isLoading.value) return;

  isLoading.value = true;
  const data = await prepareArticleFilter(queryCollection('blog'))
    .order('order', 'ASC')
    .limit(7)
    .skip(articles.value.length)
    .select('path', 'title', 'image', 'category', 'downloadUrl')
    .all();
  articles.value = articles.value.concat(data);
  isLoading.value = false;
};

useSeoMeta({
  title: t('meta.title'),
  description: t('meta.description'),
  ogTitle: t('meta.title'),
  ogDescription: t('meta.description'),
});

definePageMeta({ key: 'blog-page' });
</script>

<template>
  <main class="blogPage">
    <div class="blogPage__header">
      <NuxtLink to="/" class="blogPage__logo">
        <img :src="blogLogo" :alt="t('logo_alt')" />
      </NuxtLink>
    </div>

    <BaseToggleTransition :state="selectedCategory" :duration="500">
      <Transition name="blog-transition" mode="out-in">
        <div class="blogPage__content" v-if="filteredArticles.length" :key="selectedCategory">
          <BlogCard v-for="article in filteredArticles" :key="article.path" :article="article" />
        </div>
        <div v-else class="blogPage__notFound">{{ t('empty') }}</div>
      </Transition>
    </BaseToggleTransition>

    <BaseCollapse :visible="articlesCount > articles.length" content-class="blogPage__footer">
      <BaseButton @click="showMore" :loading="isLoading" outlined dark class="blogPage__more">
        {{ t('more') }}
      </BaseButton>
    </BaseCollapse>
  </main>
</template>

<i18n src="~/locales/blog.yaml"></i18n>
<i18n>
ru:
  logo_alt: Логотип блога
  more: Показать еще
  empty: Список статей пустой
  category_all: Все
  meta:
    title: Блог об уходе за кожей, активных ингредиентах и осознанности
    description: "Читайте статьи блога Don't Touch My Skin: обзоры активных ингредиентов (ретинол, витамин С), секреты ухода, осознанный подход к красоте и личный опыт."

en:
  logo_alt: Blog logo
  more: Show more
  empty: The list of articles is empty
  category_all: All
  meta:
    title: Blog about Skincare, Active Ingredients, and Mindfulness
    description: "Read Don't Touch My Skin blog articles: reviews of active ingredients (retinol, vitamin C), skincare secrets, mindful beauty approach, and personal experience."
</i18n>

<style lang="scss">
.blogPage {
  padding: 20px;

  @include mq('sm') {
    padding: 0;
  }

  &__header {
    padding-top: 6px;
    padding-bottom: 16px;
    display: grid;
    grid-template-columns: 1fr auto minmax(71px, 1fr);
    gap: 20px;

    @include mq('sm') {
      display: flex;
      flex-direction: column;
      padding: 0;
    }
  }

  &__logo {
    width: 188px;
    height: 182px;

    @include mq('sm') {
      margin-top: 36px;
      margin-left: 16px;
    }

    img {
      width: 100%;
      display: block;
    }
  }

  &__horizontalScroll {
    @include mq('sm') {
      overflow: auto;
      height: 62px;
      margin-bottom: 16px;
    }
  }

  &__filters {
    width: 100%;
    max-width: 652px;

    @include mq('sm') {
      max-width: initial;
      padding: 0 16px;
      width: fit-content;
    }
  }

  &__content {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
    max-width: 1600px;
    margin: 0 auto;
    padding-bottom: 60px;

    @include mq('lg') {
      grid-template-columns: repeat(2, 1fr);
    }

    @include mq('sm') {
      display: flex;
      flex-direction: column;
      padding: 0 16px;
      gap: 32px;
    }
  }

  &__footer {
    display: flex;
    justify-content: center;
    padding-bottom: 40px;
  }

  &__more {
    width: 222px;
  }

  &__notFound {
    font-size: 24px;
    line-height: 32px;
    letter-spacing: 0.2px;
    color: #121212;
    text-align: center;
    padding: 52px 0;

    @include mq('sm') {
      font-size: 20px;
      line-height: 28px;
      padding: 32px 0;
    }
  }

  .blog-transition-enter-active,
  .blog-transition-leave-active {
    transition:
      opacity 0.3s ease,
      transform 0.3s ease;
  }

  .blog-transition-enter-from,
  .blog-transition-leave-to {
    opacity: 0;
    transform: translateY(20px);
  }
}
</style>
